import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster, toast } from 'sonner';
import { useEffect, type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { directionFor } from '@lib/i18n';
import { useUIStore } from '@stores/ui';
import { useAuthStore } from '@stores/auth';
import { startTokenRefreshLoop, setForcedLogoutHandler } from '@lib/api/client';
import { queryClient } from '@lib/api/queryClient';
import { AppErrorBoundary } from '@components/error/AppErrorBoundary';

function SystemEffects() {
  const { t, i18n } = useTranslation('common');
  const applyTheme = useUIStore((s) => s.applyTheme);
  const hydrate = useAuthStore((s) => s.hydrate);
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    hydrate();
    applyTheme();
    document.documentElement.setAttribute('dir', directionFor(i18n.language));
    document.documentElement.setAttribute('lang', i18n.language);

    setForcedLogoutHandler((reason) => {
      logout();
      if (reason === 'session_terminated') {
        toast.error(
          t(
            'auth.sessionTerminatedByAdmin',
            directionFor(i18n.language) === 'rtl'
              ? 'تم إنهاء جلستك من قِبل المسؤول'
              : 'Your session was terminated by an administrator',
          ),
        );
      }
      if (window.location.pathname !== '/app/login') window.location.assign('/app/login');
    });

    startTokenRefreshLoop();
  }, [applyTheme, hydrate, i18n.language, logout, t]);

  return null;
}

export function AppProviders({ children }: PropsWithChildren) {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SystemEffects />
        {children}
        <Toaster position="top-center" richColors closeButton />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
